// Pure helper for placing hidden-stop icons along a journey segment's
// bezier connection line. The bezier is parameterised by t ∈ [0, 1];
// the icon's x is computed from the stop's time-derived x position
// relative to the segment's pixel span.
//
// Clamped to [0, 1] (NOT a narrower [0.05, 0.95] inset). A previous
// inset kept the icons off the very endpoints visually but translated
// 5% of the segment's pixel span into a time drift away from the
// cluster edge — invisible on short segments, but visible hours of
// drift on long ones (e.g. a pilot-out whose departure matches a
// terminal's, followed by the next visible cluster days later). With
// a flat [0, 1] clamp, a stop whose time matches a segment endpoint
// sits ON that endpoint, hugging the bar's edge — the icon's surface-
// fill outer ring keeps it readable against the bar underneath.

// Visual size of a hidden-stop icon. Default = the original three-ring
// target (r=8.5). Small = a single port-coloured dot, used for pilot
// stations whose informational value is low enough that they should
// recede on the journey line. The enum is the seam — if we ever need a
// second "small-treated" portFunc, only hiddenStopSize() changes.
export type HiddenStopSize = "default" | "small";

// portFunc letters that render at the small size. Pilot codes only —
// other transit-y codes (P passing, W waiting, I canal transit, F
// fueling) stay at default size because their stops carry useful
// terminal-resolution information.
export function hiddenStopSize(portFunc: string | null | undefined): HiddenStopSize {
  if (portFunc === "A" || portFunc === "B") return "small";
  return "default";
}

// Allow-list of portFunc letters that surface as hidden-stop icons on
// the journey-connection layer. Everything else (P passing, W waiting,
// I canal transit, F fueling, T terminating, Y delivery, Z redelivery,
// R repair, O other, S unknown) is filtered out at gather time —
// transit/admin-bookkeeping stops would otherwise crowd the bezier
// without telling the user anything actionable about the cargo path.
const VISIBLE_HIDDEN_STOP_FUNCS = new Set(["A", "B", "C", "D", "L"]);
export function isHiddenStopVisible(portFunc: string | null | undefined): boolean {
  return portFunc != null && VISIBLE_HIDDEN_STOP_FUNCS.has(portFunc);
}

export function tForXOnSegment(
  x1: number,
  x2: number,
  xAtTime: number,
): number {
  const spanX = x2 - x1;
  if (spanX === 0) return 0.5;
  const tRaw = (xAtTime - x1) / spanX;
  return Math.max(0, Math.min(1, tRaw));
}

// Hidden-stop icon outer radius in pixels, per render size. The default
// three-ring target's outermost circle is r=8.5; the pilot icon is the
// same silhouette with the middle ring dropped — a surface-filled ring
// at r=5 around a port-colour dot at r=2.5. groupOverlappingIcons reads
// these to decide when two icons actually visually overlap.
export const ICON_RADIUS: Record<HiddenStopSize, number> = {
  default: 8.5,
  small: 5,
};

// Derived for back-compat with any external import. Equal to the sum of
// two default radii (preserves the historical "17px" threshold for
// two default icons exactly).
export const ICON_DIAMETER_PX = ICON_RADIUS.default * 2;

// Group placed icons by visual overlap. Walks the input in iteration
// order (typically chronological), comparing each entry against the
// previous group's tail using 2D Euclidean distance against the SUM of
// the two icons' radii (small+small merges at < 10px, small+default at
// < 13.5px, default+default at < 17px).
// Horizontal-only Math.abs(Δtx) would falsely collapse two icons
// clamped to opposite endpoints of a narrow segment on DIFFERENT lanes
// — the BREMERHAVEN pilot-in/pilot-out merge bug.
//
// Pure-function extraction so the regression scenarios can be locked in
// by tests without spinning up a Solid render harness; the call site
// in CalendarTimeline.renderConnections delegates here verbatim.
export function groupOverlappingIcons<
  T extends { tx: number; ty: number; size: HiddenStopSize },
>(placed: readonly T[]): T[][] {
  const groups: T[][] = [];
  for (const p of placed) {
    const last = groups[groups.length - 1];
    const ref = last?.[last.length - 1];
    const threshold = ref ? ICON_RADIUS[p.size] + ICON_RADIUS[ref.size] : 0;
    const overlaps = ref !== undefined
      && Math.hypot(p.tx - ref.tx, p.ty - ref.ty) < threshold;
    if (overlaps) last!.push(p);
    else groups.push([p]);
  }
  return groups;
}
